import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Music2, Disc3 } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] flex">
      {/* Left side - Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/8448561/pexels-photo-8448561.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#FF007F] rounded-full flex items-center justify-center">
              <Disc3 className="w-7 h-7 text-black" />
            </div>
            <span className="text-2xl font-bold text-white font-['Outfit']">MusicBox</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight mb-4">
            Join the<br />Experience
          </h1>
          <p className="text-lg text-gray-400 max-w-md">
            Create an account to access curated playlists and generate your perfect mix.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#FF007F] rounded-full flex items-center justify-center">
              <Music2 className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">MusicBox</span>
          </div>

          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">Create account</h2>
          <p className="text-gray-400 mb-8">Get started with your DJ playlist experience</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF007F] focus:ring-[#FF007F]/20 h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF007F] focus:ring-[#FF007F]/20 h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password-input"
                className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF007F] focus:ring-[#FF007F]/20 h-12 rounded-xl"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-12 bg-[#FF007F] hover:bg-[#CC0066] text-black font-semibold rounded-full transition-all hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(245,158,11,0.3)]"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FF007F] hover:text-[#CC0066] font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
